import { useState } from "react";
import { Card, Text, Group, Badge, Button, Select } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { supportApi } from "../../api/support";
import { QueryFormModal, type QueryCreateBody } from "./QueryFormModal";
import { type SupportQuery, refName, refPhone } from "../../types/domain";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable, type Column } from "../../components/common/DataTable";
import { SearchInput } from "../../components/common/SearchInput";
import {
  ListDateRangeFilter,
  type DateRange,
} from "../../components/common/ListDateRangeFilter";
import { ExportMenu } from "../../components/common/ExportMenu";
import { collectAll, type ExportField } from "../../lib/export";
import { StatusBadge } from "../../components/common/StatusBadge";
import { TripLink } from "../../components/common/TripLink";
import { ZoomableImage } from "../../components/common/ZoomableImage";
import {
  DetailDrawer,
  DetailField,
  DetailSection,
} from "../../components/common/DetailDrawer";
import { SimpleGrid } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useAuth } from "../../auth/useAuth";
import { StatusUpdater } from "../../components/common/StatusUpdater";
import { humanize, toSelectOptions } from "../../lib/labels";
import { formatDateTime } from "../../lib/format";
import { errorMessage } from "../../lib/axios";

const QUERY_STATUSES = ["pending", "inProgress", "completed"];
const QUERY_STATUS_OPTIONS = toSelectOptions(QUERY_STATUSES, "All statuses");

const queryExportFields: ExportField<SupportQuery>[] = [
  { label: "Ticket", value: (q) => q.ticketNo ?? q._id },
  { label: "Type", value: (q) => q.queryType },
  { label: "Message", value: (q) => q.message },
  { label: "Raised by", value: (q) => refName(q.createdByInfo?.createdBy) },
  { label: "User type", value: (q) => q.createdByInfo?.userType },
  { label: "Status", value: (q) => q.status },
  { label: "Received", value: (q) => q.createdAt },
];

export function QueriesPage() {
  const qc = useQueryClient();
  const { admin } = useAuth();
  const [selected, setSelected] = useState<SupportQuery | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [range, setRange] = useState<DateRange>({});
  const filters = { status, startDate: range.startDate, endDate: range.endDate };
  const { page, setPage, search, onSearchChange, result, isLoading, error } =
    usePaginatedList<SupportQuery>({
      queryKey: ["queries"],
      fetcher: supportApi.listQueries,
      filters,
    });

  const createMutation = useMutation({
    mutationFn: (body: QueryCreateBody) => supportApi.createQuery(body),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Query created" });
      setFormOpen(false);
      void qc.invalidateQueries({ queryKey: ["queries"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status, comment }: { id: string; status: string; comment: string }) =>
      supportApi.updateQueryStatus(id, {
        status,
        comment,
        updatedBy: admin?.id ?? "",
      }),
    onSuccess: (updated) => {
      notifications.show({ color: "green", message: "Status updated" });
      setSelected(updated);
      void qc.invalidateQueries({ queryKey: ["queries"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const columns: Column<SupportQuery>[] = [
    {
      key: "ticket",
      header: "Ticket",
      render: (q) => (
        <Text size="sm" ff="monospace">
          {q.ticketNo ?? q._id.slice(-8)}
        </Text>
      ),
    },
    { key: "type", header: "Type", render: (q) => humanize(q.queryType) },
    {
      key: "message",
      header: "Message",
      render: (q) => (
        <Text size="sm" c="dimmed" lineClamp={1} maw={320}>
          {q.message ?? "—"}
        </Text>
      ),
    },
    {
      key: "by",
      header: "Raised by",
      render: (q) => (
        <Group gap={6} wrap="nowrap">
          <Badge size="sm" variant="light">
            {humanize(q.createdByInfo?.userType)}
          </Badge>
          <Text size="sm">{refName(q.createdByInfo?.createdBy) ?? "—"}</Text>
        </Group>
      ),
    },
    { key: "trip", header: "Trip", render: (q) => <TripLink trip={q.tripID} /> },
    { key: "status", header: "Status", render: (q) => <StatusBadge status={q.status} /> },
    {
      key: "received",
      header: "Received",
      render: (q) => (
        <Text size="xs" c="dimmed">
          {formatDateTime(q.createdAt)}
        </Text>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Support Queries"
        subtitle="Rider & driver tickets"
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setFormOpen(true)}
          >
            New query
          </Button>
        }
      />
      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <SearchInput
            value={search}
            onChange={onSearchChange}
            placeholder="Search message…"
          />
          <Select
            data={QUERY_STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v ?? "");
              setPage(1);
            }}
            w={160}
            allowDeselect={false}
          />
          <ListDateRangeFilter
            value={range}
            onChange={(r) => {
              setRange(r);
              setPage(1);
            }}
          />
          <ExportMenu
            filename="queries"
            fields={queryExportFields}
            fetchAll={() =>
              collectAll(supportApi.listQueries, {
                search: search || undefined,
                ...filters,
              })
            }
          />
        </Group>
        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(q) => q._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No support queries"
          onRowClick={setSelected}
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>

      <DetailDrawer
        opened={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.ticketNo ?? "Query"}
      >
        {selected && (
          <>
            <DetailSection title="Query">
              <DetailField label="Type" value={humanize(selected.queryType)} />
              <DetailField label="Status" value={<StatusBadge status={selected.status} />} />
              <DetailField label="Received" value={formatDateTime(selected.createdAt)} />
              <DetailField label="Message" value={selected.message} />
            </DetailSection>
            <DetailSection title="Raised by">
              <DetailField label="Role" value={humanize(selected.createdByInfo?.userType)} />
              <DetailField label="Name" value={refName(selected.createdByInfo?.createdBy)} />
              <DetailField label="Phone" value={refPhone(selected.createdByInfo?.createdBy)} />
              <DetailField label="Trip" value={<TripLink trip={selected.tripID} />} />
            </DetailSection>
            {!!selected.images?.length && (
              <DetailSection title="Attachments">
                <SimpleGrid cols={2}>
                  {selected.images.map((src, i) => (
                    <ZoomableImage key={i} src={src} h={120} title={`${selected.ticketNo} — attachment ${i + 1}`} />
                  ))}
                </SimpleGrid>
              </DetailSection>
            )}
            <DetailSection title="Manage">
              <StatusUpdater
                current={selected.status}
                statuses={QUERY_STATUSES}
                loading={updateStatus.isPending}
                onSubmit={(status, comment) =>
                  updateStatus.mutate({ id: selected._id, status, comment })
                }
              />
            </DetailSection>
          </>
        )}
      </DetailDrawer>

      <QueryFormModal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        loading={createMutation.isPending}
        onSubmit={(body) => createMutation.mutate(body)}
      />
    </>
  );
}
